import { lazy, Suspense, useEffect, useState } from "react";
import { ThemeProvider } from "@mui/material";
import { Route, useLocation } from "react-router-dom";
import theme from "./theme";
import { ToastContainer } from "react-toastify";
import { Loader, PopConfirm, PrivateRoute, SuspenseLoader } from "@/components";
import routes from "./router";
import { ROUTES } from "./constants";
import "./app.css";
import ProjectStatus from "./pages/project-status";
import EditHub from "./pages/edit-hub";
import { datadogRum } from "@datadog/browser-rum";
import { Routes } from "@datadog/browser-rum-react/react-router-v6";
import Cookies from "js-cookie";
import { env, backendURL } from "./config";
import { t } from "i18next";

const Login = Loader(lazy(() => import("./pages/login")));

const App = () => {
  const location = useLocation();
  const [updateData, setUpdateData] = useState<ServiceWorkerRegistration>();
  const [isUpdateVersionModalOpen, setIsUpdateVersionModalOpen] = useState(false);

  const handleConfirmUpdate = (registration: ServiceWorkerRegistration) => {
    if (registration.waiting) {
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
    }
  };


  useEffect(() => {
    const handlePWAUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<ServiceWorkerRegistration>;
      setUpdateData(customEvent.detail);
      setIsUpdateVersionModalOpen(true)
    };

    window.addEventListener("PWA_UPDATE_AVAILABLE", handlePWAUpdate);

    return () =>
      window.removeEventListener("PWA_UPDATE_AVAILABLE", handlePWAUpdate);
  }, []);

  // this part is use to debug on iphone. it work only when we run it local and only on iphone
  useEffect(() => {
    const isiPhone = /iPhone|iPad|iPod/.test(navigator.userAgent);
    console.log(`backendURL=${backendURL}`);
    console.log(`env=${env}`);
    console.log(`process.env.NODE_ENV=${process.env.NODE_ENV}`);
    console.log(`isiPhone=${isiPhone}`);
    if (process.env.NODE_ENV !== "production" && isiPhone) {
      const script = document.createElement("script");
      script.src = "https://cdn.jsdelivr.net/npm/eruda";
      console.log("load from https://cdn.jsdelivr.net/npm/eruda");
      script.onload = () => {
        // @ts-ignore
        eruda.init();
      };
      document.body.appendChild(script);
      console.log("finish load from eruda");
    }
  }, []);

  useEffect(() => {
    const user = JSON.parse(Cookies.get("user") || null);
    if (user) {
      datadogRum.setUser({
        id: user.id,
        name: user.name,
        email: user.email,
        customer_id: user.customer_id,
      });
    }
  }, [location]);

  return (
    <ThemeProvider theme={theme}>
      <Suspense fallback={<SuspenseLoader />}>
        <Routes>
          <Route element={<PrivateRoute />}>
            {routes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ))}
          </Route>

          <Route path={ROUTES.LOGIN} element={<Login />} />
          <Route path={ROUTES.PROJECT_STATUS} element={<ProjectStatus />} />
          <Route path={ROUTES.EDIT_HUB} element={<EditHub />} />
        </Routes>
      </Suspense>

      <ToastContainer
        closeOnClick
        autoClose={2000}
        position="top-center"
        theme="colored"
        pauseOnHover
      />
      <PopConfirm
        isOpen={isUpdateVersionModalOpen}
        onConfirm={() => handleConfirmUpdate(updateData)}
        onCancel={() => setIsUpdateVersionModalOpen(false)}
        confirmText={t("update")}
        withCancelBtn={false}
        title={`${t("newVersionAvailable")}!`}
        subTitle={t("newVersionConfirmSubtitle")}
      />
    </ThemeProvider>
  );
};

export default App;
